class Collection {
  constructor() {
    this.section = null;
    this.productContainer = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalItems = 0;
    this.pageSize = 24;
    this.paginationType = 'paginate';
    this.enableFilters = false;
    this.enableSorting = false;
    this.loadingMore = false;
    this.closeTimer = null;
    this.loadedProductIds = new Set();
    this.soldOutBuffer = [];
    this.nextPagePromise = null;
    this.nextPagePromisePage = 0;
    this.sourceExhausted = false;
    this.infiniteLoadingObserver = null;
    this.prefetchObserver = null;

  }

  init = () => {
    this.infiniteLoadingObserver?.disconnect();
    this.prefetchObserver?.disconnect();

    this.section = document.querySelector('.facest-filters-section');
    if (!this.section) return;

    this.productContainer = this.section.querySelector('[data-product-container]');
    this.paginationType = this.section.dataset.paginationType || 'paginate';
    this.totalPages = this.productContainer ? Number(this.productContainer.dataset.totalPages || 1) : 1;
    this.totalItems = this.productContainer ? Number(this.productContainer.dataset.totalItems || 0) : 0;
    this.pageSize = this.productContainer ? Number(this.productContainer.dataset.pageSize || 24) : 24;
    this.currentPage = this.productContainer ? Number(this.productContainer.dataset.currentPage || new URL(window.location.href).searchParams.get('page') || 1) : 1;
    this.enableFilters = this.section.dataset.enableFilters === 'true';
    this.enableSorting = this.section.dataset.enableSorting === 'true';

    this.initFilters();
    this.initSortingPopup();
    this.prepareInitialProducts();


    this.initLoadMore();
    this.initProducts();
  };

  initFilters = () => {
    if (!this.enableFilters) return;

    const modal = this.section.querySelector('[data-tanatel-filter-modal]') || this.section.querySelector('.m-sidebar');
    if (!modal) return;
    const content = modal.querySelector('.tanatel-filter-modal__content, .m-sidebar--content');
    if (!content) return;

    this.section.querySelectorAll('.tanatel-filter-open, .m-sidebar--open').forEach((opener) => {
      if (opener.dataset.tanatelBound === 'true') return;
      opener.dataset.tanatelBound = 'true';
      opener.addEventListener('click', () => this.openModal(modal, content, opener));
    });

    modal.querySelectorAll('.tanatel-filter-modal__close, .m-sidebar--close').forEach((closer) => {
      if (closer.dataset.tanatelBound === 'true') return;
      closer.dataset.tanatelBound = 'true';
      closer.addEventListener('click', () => this.closeModal(modal));
    });

    if (modal.dataset.tanatelBound !== 'true') {
      modal.dataset.tanatelBound = 'true';
      modal.addEventListener('click', (event) => {
        if (event.target === modal) this.closeModal(modal);
      });
    }
  };

  openModal = (modal, content, opener) => {
    this.closeSorting();
    window.clearTimeout(this.closeTimer);
    modal.style.display = 'block';
    modal.classList.add('is-open');
    opener.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('tanatel-collection-modal-open');
    window.requestAnimationFrame(() => content.focus());
  };

  closeModal = (modal) => {
    if (!modal) return;
    modal.classList.remove('is-open');
    this.section.querySelectorAll('.tanatel-filter-open, .m-sidebar--open').forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('tanatel-collection-modal-open');
    this.closeTimer = window.setTimeout(() => {
      if (!modal.classList.contains('is-open')) modal.style.display = 'none';
    }, 260);
  };

  initSortingPopup = () => {
    if (!this.enableSorting) return;
    const wrapper = this.section.querySelector('.m-sortby-mobile--wrapper');
    const content = this.section.querySelector('.m-sortby-mobile--content');
    if (!wrapper || !content) return;

    this.section.querySelectorAll('.m-sortby--open').forEach((opener) => {
      if (opener.dataset.tanatelBound === 'true') return;
      opener.dataset.tanatelBound = 'true';
      opener.addEventListener('click', () => this.openSorting(opener, wrapper, content));
    });

    const close = wrapper.querySelector('.m-sortby-mobile--close');
    if (close && close.dataset.tanatelBound !== 'true') {
      close.dataset.tanatelBound = 'true';
      close.addEventListener('click', () => this.closeSorting());
    }

    if (wrapper.dataset.tanatelBound !== 'true') {
      wrapper.dataset.tanatelBound = 'true';
      wrapper.addEventListener('click', (event) => {
        if (event.target === wrapper) this.closeSorting();
      });
    }

    wrapper.querySelectorAll('.m-sortby-mobile--item').forEach((item) => {
      if (item.dataset.tanatelBound === 'true') return;
      item.dataset.tanatelBound = 'true';
      item.addEventListener('click', () => this.applySorting(item.dataset.value));
    });
  };

  openSorting = (opener, wrapper, content) => {
    const filterModal = this.section.querySelector('[data-tanatel-filter-modal]');
    if (filterModal) this.closeModal(filterModal);
    wrapper.style.display = 'block';
    wrapper.classList.add('is-open');
    opener.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('tanatel-collection-modal-open');
    window.requestAnimationFrame(() => content.focus());
  };

  closeSorting = () => {
    const wrapper = this.section && this.section.querySelector('.m-sortby-mobile--wrapper');
    if (!wrapper) return;
    wrapper.classList.remove('is-open');
    this.section.querySelectorAll('.m-sortby--open').forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('tanatel-collection-modal-open');
    window.setTimeout(() => {
      if (!wrapper.classList.contains('is-open')) wrapper.style.display = 'none';
    }, 260);
  };

  applySorting = (value) => {
    if (!value) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  hasExplicitAvailabilityFilter = () => {
    return new URL(window.location.href).searchParams.has('filter.v.availability');
  };

  getProductId = (item) => {
    if (!item) return '';
    const card = item.matches?.('[data-product-id]')
      ? item
      : item.querySelector?.('[data-product-id]');
    return card?.dataset?.productId || '';
  };

  isSoldOutItem = (item) => {
    if (!item) return false;

    if (item.dataset?.tanatelAvailability) {
      return item.dataset.tanatelAvailability === 'sold-out';
    }

    const card = item.querySelector?.('[data-product-id]');
    const soldOutButton = card?.querySelector?.(
      '[disabled][name="add"], .m-add-to-cart[disabled], .m-product-form__submit[disabled]'
    );

    return Boolean(soldOutButton);
  };

  rememberProduct = (item) => {
    const id = this.getProductId(item);
    if (!id) return true;
    if (this.loadedProductIds.has(id)) return false;
    this.loadedProductIds.add(id);
    return true;
  };

  bufferSoldOut = (item) => {
    if (!item || !this.rememberProduct(item)) return;
    this.soldOutBuffer.push(item);
  };

  appendAvailable = (item) => {
    if (!item || !this.productContainer || !this.rememberProduct(item)) return;
    this.productContainer.appendChild(item);
  };

  flushSoldOutBuffer = () => {
    if (!this.productContainer || !this.soldOutBuffer.length) return;

    const fragment = document.createDocumentFragment();
    this.soldOutBuffer.forEach((item) => fragment.appendChild(item));
    this.soldOutBuffer = [];
    this.productContainer.appendChild(fragment);
  };

  prepareInitialProducts = () => {
    if (!this.productContainer) return;

    this.loadedProductIds.clear();
    this.soldOutBuffer = [];
    this.nextPagePromise = null;
    this.nextPagePromisePage = 0;
    this.sourceExhausted = this.currentPage >= this.totalPages;

    const currentItems = Array.from(
      this.productContainer.querySelectorAll(':scope > .m-product-item')
    );


    // Legacy Tanatel availability behavior — unchanged.
    // Keep available products visible immediately.
    // Sold-out products are buffered and only released after every available
    // source page has been consumed. This avoids a second initial render.
    currentItems.forEach((item) => {
      if (this.isSoldOutItem(item) && !this.hasExplicitAvailabilityFilter()) {
        item.remove();
        this.bufferSoldOut(item);
      } else {
        this.rememberProduct(item);
      }
    });

    if (this.sourceExhausted) {
      this.flushSoldOutBuffer();
    }
  };

  buildSourcePageUrl = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    url.searchParams.set('section_id', this.section.dataset.sectionId);
    if (page > 1) url.searchParams.set('page', String(page));
    return url.toString();
  };

  fetchSourcePage = async (page) => {
    const response = await fetch(this.buildSourcePageUrl(page), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      throw new Error(`Collection page request failed: ${response.status}`);
    }

    const markup = await response.text();
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const container = doc.querySelector('[data-product-container]');

    return {
      page,
      totalPages: container ? Number(container.dataset.totalPages || this.totalPages) : this.totalPages,
      items: container
        ? Array.from(container.querySelectorAll(':scope > .m-product-item'))
        : []
    };
  };

  prefetchNextPage = () => {

    if (
      this.loadingMore ||
      this.sourceExhausted ||
      this.currentPage >= this.totalPages
    ) return null;

    const nextPage = this.currentPage + 1;

    if (this.nextPagePromise && this.nextPagePromisePage === nextPage) {
      return this.nextPagePromise;
    }

    this.nextPagePromisePage = nextPage;
    this.nextPagePromise = this.fetchSourcePage(nextPage)
      .catch((error) => {
        this.nextPagePromise = null;
        this.nextPagePromisePage = 0;
        throw error;
      });

    return this.nextPagePromise;
  };

  consumeSourcePage = async (page) => {
    let source;

    if (this.nextPagePromise && this.nextPagePromisePage === page) {
      source = await this.nextPagePromise;
    } else {
      source = await this.fetchSourcePage(page);
    }

    this.nextPagePromise = null;
    this.nextPagePromisePage = 0;

    if (source.totalPages) this.totalPages = source.totalPages;

    const availableFragment = document.createDocumentFragment();

    source.items.forEach((item) => {
      if (!this.rememberProduct(item)) return;

      if (!this.hasExplicitAvailabilityFilter() && this.isSoldOutItem(item)) {
        this.soldOutBuffer.push(item);
      } else {
        availableFragment.appendChild(item);
      }
    });

    if (this.productContainer) {
      this.productContainer.appendChild(availableFragment);
    }

    this.currentPage = page;
    if (this.productContainer) {
      this.productContainer.dataset.currentPage = String(page);
    }

    this.sourceExhausted = this.currentPage >= this.totalPages;

    if (this.sourceExhausted) {
      this.flushSoldOutBuffer();
    }

    this.initProducts();

    if (window.MinimogEvents) {
      window.MinimogEvents.emit('ON_PRODUCT_LIST_UPDATED');
    }

    return true;
  };

  scheduleIdlePrefetch = () => {
    if (
      this.paginationType !== 'infinite' ||
      this.sourceExhausted ||
      this.currentPage >= this.totalPages
    ) return;

    const warm = () => this.prefetchNextPage();

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warm, { timeout: 1200 });
    } else {
      window.setTimeout(warm, 450);
    }
  };

  initLoadMore = () => {
    if (
      !this.productContainer ||
      this.paginationType === 'paginate' ||
      this.totalPages <= 1 ||
      this.section.dataset.view === 'search'
    ) {
      if (this.sourceExhausted) this.flushSoldOutBuffer();
      return;
    }

    const loadMore = this.section.querySelector('[data-load-more]');
    if (!loadMore || loadMore.dataset.tanatelBound === 'true') return;

    loadMore.dataset.tanatelBound = 'true';
    loadMore.addEventListener('click', this.loadMoreProducts);

    if (this.paginationType === 'infinite' && 'IntersectionObserver' in window) {
      // Prefetch well before the customer reaches the end.
      this.prefetchObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.prefetchNextPage();
        }
      }, {
        threshold: 0,
        rootMargin: '1600px 0px'
      });

      this.prefetchObserver.observe(loadMore);

      // Consume only when the user is close enough to need the products.
      this.infiniteLoadingObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMoreProducts();
        }
      }, {
        threshold: 0,
        rootMargin: '700px 0px'
      });

      this.infiniteLoadingObserver.observe(loadMore);
      this.scheduleIdlePrefetch();
    }
  };

  loadMoreProducts = async () => {
    if (this.loadingMore || this.sourceExhausted) return;

    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) {
      this.sourceExhausted = true;
      this.flushSoldOutBuffer();
      return;
    }

    const loadMore = this.section.querySelector('[data-load-more]');
    this.loadingMore = true;

    // For infinite scroll we intentionally avoid showing a spinner:
    // the next page should already be prefetched before it is needed.
    if (this.paginationType !== 'infinite') {
      loadMore?.classList.add('m-spinner-loading');
    }

    try {
      await this.consumeSourcePage(nextPage);

      if (this.sourceExhausted && loadMore) {
        const wrapper = loadMore.parentNode;
        wrapper?.remove();
        this.infiniteLoadingObserver?.disconnect();
        this.prefetchObserver?.disconnect();
      } else {
        this.scheduleIdlePrefetch();
      }
    } catch (_) {
      // Keep the current storefront untouched if a background request fails.
    } finally {
      this.loadingMore = false;
      loadMore?.classList.remove('m-spinner-loading');
    }
  };

  initProducts = () => {
    if (window.MinimogTheme && window.MinimogTheme.CompareProduct) window.MinimogTheme.CompareProduct.setCompareButtonsState();
    if (window.MinimogTheme && window.MinimogTheme.Wishlist) window.MinimogTheme.Wishlist.setWishlistButtonsState();
  };

}

window.Collection = Collection;
